//
// Copyright 2025 DXOS.org
//

import { type EditorView } from '@codemirror/view';

import type * as Theme from '@dxos/react-ui/Theme';
import { insertAtCursor } from '@dxos/ui-editor';
import { type ChromaticPalette } from '@dxos/ui-types';
import { type MaybePromise } from '@dxos/util';

export type EditorMenuGroup = {
  id: string;
  label?: Theme.Label;
  items: EditorMenuItem[];
};

export type EditorMenuItem = {
  id: string;
  label: Theme.Label;
  icon?: string;
  iconHue?: ChromaticPalette;
  onSelect?: (event: { view: EditorView; head: number }) => MaybePromise<void>;
};

export const getMenuItem = (groups: EditorMenuGroup[], id?: string): EditorMenuItem | undefined => {
  return groups.flatMap((group) => group.items).find((item) => item.id === id);
};

/** Undefined when the menu is empty; at either end the current item is returned unchanged. */
export const getNextMenuItem = (groups: EditorMenuGroup[], id?: string): EditorMenuItem | undefined => {
  const items = groups.flatMap((group) => group.items);
  const index = items.findIndex((item) => item.id === id);
  return index < items.length - 1 ? items[index + 1] : items[index];
};

/** Undefined when the menu is empty; at either end the current item is returned unchanged. */
export const getPreviousMenuItem = (groups: EditorMenuGroup[], id?: string): EditorMenuItem | undefined => {
  const items = groups.flatMap((group) => group.items);
  const index = items.findIndex((item) => item.id === id);
  return index > 0 ? items[index - 1] : items[index];
};

export const createMenuGroup = ({
  id = 'menu',
  label,
  filter,
  items,
}: {
  id?: string;
  label?: Theme.Label;
  filter?: string;
  items: string[];
}): EditorMenuGroup => ({
  id,
  label,
  items: items
    .filter((item) => !filter || item.toLowerCase().includes(filter.toLowerCase()))
    .map((item, i) => ({
      id: `${id}-${i}`,
      label: item,
      onSelect: ({ view, head }) => insertAtCursor(view, head, item),
    })),
});

export const filterMenuGroups = (
  groups: EditorMenuGroup[],
  filter: (item: EditorMenuItem) => boolean,
): EditorMenuGroup[] => {
  return groups.map((group) => ({
    ...group,
    items: group.items.filter(filter),
  }));
};
